import { useDispatch, useSelector } from 'react-redux';
import {
  addTicket as addT,
  removeTicket as removeT,
  removeAllTickets as removeAllT,
  setAllTickets as setAllT,
  upsertManyTickets as upsertManyT,
  removeManyTickets as removeManyT,
} from './tickets-selected.slice';
import { useTicketTypes } from '@/store';

const useTicketsSelected = () => {
  const dispatch = useDispatch();
  const { getByTicketTypeID, getByCategoryID } = useTicketTypes();

  const ticketsSelected = useSelector((state) =>
    state.ticketsSelected.ids.map((id) => state.ticketsSelected.entities[id]),
  );

  const ticketsSelectedEntities = useSelector(
    (state) => state.ticketsSelected.entities,
  );

  const hasTickets = useSelector(
    (state) => state.ticketsSelected.ids.length > 0,
  );

  const count = useSelector((state) => {
    if (state.ticketsSelected.ids.length > 0) {
      let counter = 0;
      Object.values(state.ticketsSelected.entities).map((entity) => {
        counter += entity.quantity;
      });
      return counter;
    }
    return 0;
  });

  const ticketTypesCategoriesSelecteds = () => {
    const categories = new Set(
      ticketsSelected.map((ticket) => ticket.category.name),
    );
    return [...categories];
  };

  const parseSeat = (seat, ticketTypeSelected) => {
    const { id, selectedTicketType, category } = seat;
    let ticketType = {};
    let currentPrice = 0;

    if (ticketTypeSelected) {
      const { ticketType: ticketTypeIDStr } = ticketTypeSelected;
      const ticketTypeID = parseInt(ticketTypeIDStr, 10);
      ticketType = getByTicketTypeID(ticketTypeID) || {};

      const SeatsIOTicketType =
        category.pricing.ticketTypes.find(
          (SeatsTicketType) =>
            SeatsTicketType.ticketType === selectedTicketType,
        ) || {};

      currentPrice = SeatsIOTicketType.price || 0;
    } else {
      ticketType = getByCategoryID(category.key);
      currentPrice = category.pricing.price;
    }

    let numSeats = 1;

    if (seat.objectType === 'Table' && seat.seats) {
      numSeats = seat.seats.length;
    }

    return {
      id,
      seatsId: id,
      ticket_type_id: ticketType.ticket_type_id,
      name: ticketType.name,
      price: currentPrice,
      category: {
        id: ticketType.category_id,
        name: ticketType.category_name,
        color: ticketType.category_color,
      },
      seatsIoInfo: seat.labels,
      quantity: 1,
      min: ticketType.min_per_sale,
      max: ticketType.max_per_sale,
      availability: ticketType.availability,
      objectType: seat.objectType,
      parent: seat.parent,
      numSeats,
      zone: ticketType.zone,
    };
  };

  const parseTicketType = (
    {
      ticket_type_id,
      name,
      max_price: price,
      category_name,
      category_id,
      category_color,
      min_per_sale,
      max_per_sale,
      availability,
      zone,
    },
    quantity = 0,
  ) => {
    return {
      id: ticket_type_id,
      ticket_type_id,
      name,
      price,
      category: {
        id: category_id,
        name: category_name,
        color: category_color,
      },
      seatsIoInfo: null,
      quantity,
      min: min_per_sale,
      max: max_per_sale,
      availability,
      zone,
    };
  };

  const parseGeneralAdmissionArea = (
    generalAdmission,
    ticketTypeIDStr,
    quantity = 0,
  ) => {
    const { category, id } = generalAdmission;
    const ticketTypeID = parseInt(ticketTypeIDStr, 10);
    const ticketType = getByTicketTypeID(ticketTypeID) || {};
    let SeatsIOTicketTypePrice = 0;

    if (category.pricing.ticketTypes) {
      const SeatsIOTicketType =
        category.pricing.ticketTypes.find(
          (SeatsTicketType) => SeatsTicketType.ticketType === ticketTypeIDStr,
        ) || {};
      SeatsIOTicketTypePrice = SeatsIOTicketType.price;
    } else {
      SeatsIOTicketTypePrice = category.pricing.price;
    }

    return {
      id: getGeneralAdmissionAreaID(generalAdmission, ticketTypeID),
      seatsId: id,
      ticket_type_id: ticketTypeID,
      name: ticketType.name,
      price: SeatsIOTicketTypePrice,
      category: {
        id: ticketType.category_id,
        name: ticketType.category_name,
        color: ticketType.category_color,
      },
      seatsIoInfo: generalAdmission.labels,
      quantity,
      objectType: generalAdmission.objectType,
      min: ticketType.min_per_sale,
      max: ticketType.max_per_sale,
      availability: ticketType.availability,
    };
  };

  const addTicket = (ticket) => {
    dispatch(addT(ticket));
  };

  const removeTicket = (id) => {
    dispatch(removeT(id));
  };

  const removeAllTickets = () => {
    dispatch(removeAllT());
  };

  const upsertManyTickets = (tickets) => {
    dispatch(upsertManyT(tickets));
  };

  const removeManyTickets = (tickets) => {
    dispatch(removeManyT(tickets));
  };

  const setAllTickets = (tickets) => {
    dispatch(setAllT(tickets));
  };

  const addTicketTypeQuantity = (ticketType, quantity) => {
    addTicket(parseTicketType(ticketType, quantity));
  };

  const removeTicketType = (ticketType) => {
    removeTicket(ticketType.ticket_type_id);
  };

  const getGeneralAdmissionAreaID = (generalAdmission, ticketType) =>
    `[${generalAdmission.id}]-[${ticketType}]`;

  const getTicketTypesFromGeneralAdmissionArea = (generalAdmission) => {
    let ticketTypesToRemove = [];
    let ticketTypesToUpsert = [];
    if (
      generalAdmission.selectionPerTicketType &&
      Object.keys(generalAdmission.selectionPerTicketType).length > 0
    ) {
      Object.keys(generalAdmission.selectionPerTicketType).map((ticketType) => {
        if (generalAdmission.selectionPerTicketType[ticketType] === 0) {
          ticketTypesToRemove = [
            ...ticketTypesToRemove,
            getGeneralAdmissionAreaID(generalAdmission, ticketType),
          ];
        } else {
          ticketTypesToUpsert = [
            ...ticketTypesToUpsert,
            parseGeneralAdmissionArea(
              generalAdmission,
              ticketType,
              generalAdmission.selectionPerTicketType[ticketType],
            ),
          ];
        }
      });
    } else {
      const ticketType = getByCategoryID(generalAdmission.category.key);
      if (generalAdmission.numSelected === 0) {
        ticketTypesToRemove = [
          getGeneralAdmissionAreaID(
            generalAdmission,
            ticketType.ticket_type_id,
          ),
        ];
      } else {
        ticketTypesToUpsert = [
          parseGeneralAdmissionArea(
            generalAdmission,
            ticketType.ticket_type_id,
            generalAdmission.numSelected,
          ),
        ];
      }
    }

    return {
      ticketTypesToRemove,
      ticketTypesToUpsert,
    };
  };

  const updateGeneralAdmissionArea = (generalAdmission) => {
    const { ticketTypesToRemove, ticketTypesToUpsert } =
      getTicketTypesFromGeneralAdmissionArea(generalAdmission);
    upsertManyTickets(ticketTypesToUpsert);
    removeManyTickets(ticketTypesToRemove);
    return ticketTypesToUpsert;
  };

  const addSeatsIOTicket = (seat, selectedTicketType) => {
    if (seat.objectType === 'Seat' || seat.objectType === 'Table') {
      const currentSeat = parseSeat(seat, selectedTicketType);
      addTicket(currentSeat);
      return currentSeat;
    } else if (seat.objectType === 'GeneralAdmissionArea') {
      return updateGeneralAdmissionArea(seat);
    }
  };
  const removeSeatsIOTicket = (seat) => {
    if (seat.objectType === 'Seat' || seat.objectType === 'Table') {
      removeTicket(seat.id);
    } else if (seat.objectType === 'GeneralAdmissionArea') {
      updateGeneralAdmissionArea(seat);
    }
  };

  const getTicketsSelectedByType = (ticketType) => {
    console.log(ticketsSelectedEntities);
    return ticketsSelectedEntities[ticketType];
  };

  return {
    ticketsSelected,
    hasTickets,
    count,
    addTicketTypeQuantity,
    removeTicketType,
    removeTicket,
    addSeatsIOTicket,
    removeSeatsIOTicket,
    getTicketsSelectedByType,
    removeAllTickets,
    ticketTypesCategoriesSelecteds,
  };
};

export default useTicketsSelected;
